// "Hand this to the person who will drain the queue" — the bundle download.
//
// An admin downloads a zip holding this practice's APPROVED runbook, a README with the
// exact commands, the API-key scope the run needs and where to create one, and the
// pinned atlas-billing-ops revision it was built for. The skill itself is not in the
// zip: it lives in the other repo and the README says how to check it out at that
// revision. The runner refuses a runbook version other than the one Atlas holds, which
// is why a downloaded runbook is a reading aid and never an input.
//
// When there is no approved runbook this panel is the explanation and a link to the
// discovery instructions — not a disabled button with no reason.
//
// PHI: nothing. A runbook is URLs and a field map; the README is commands.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { downloadSkillBundle, fetchSkillBundle, portalLabel } from "./api";
import { relativeTime } from "./ageing";
import "./Queue.css";

// What to say for each reason the server gives, beyond its own sentence.
const NEXT_STEP = {
  no_portal: "Set the practice's submit portal, then post a runbook for it.",
  no_runbook: "Run discovery on the portal to write version 1.",
  not_approved: "Read it on the Runbook tab above, then approve it.",
};

export default function SkillBundlePanel({ practiceId, isAdmin = false }) {
  const [bundle, setBundle] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const ticket = useRef(0);

  const load = useCallback(async () => {
    if (!practiceId || !isAdmin) return;
    const mine = (ticket.current += 1);
    setLoading(true);
    setError(null);
    try {
      const body = await fetchSkillBundle(practiceId);
      if (mine !== ticket.current) return;
      setBundle(body);
    } catch (err) {
      if (mine !== ticket.current) return;
      setError(err.message);
      // Never leave the previous practice's bundle under this practice's name.
      setBundle(null);
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
  }, [practiceId, isAdmin]);

  useEffect(() => {
    setBundle(null);
    setDownloadError(null);
    load();
  }, [load]);

  // A fetch, not a link: see `downloadSkillBundle`. A failure has to be SAID — a
  // download that silently does nothing is the worst outcome for a page whose whole
  // job is handing someone a file.
  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadSkillBundle(practiceId);
    } catch (err) {
      setDownloadError(err.message);
      // The runbook may have been superseded or unapproved since this panel loaded.
      load();
    } finally {
      setDownloading(false);
    }
  };

  // Not an admin: the routes are admin-only and would 403, so say nothing rather than
  // show a control that cannot work.
  if (!isAdmin) return null;

  return (
    <section className="sq-bundle" aria-label="Skill bundle">
      <h3>Give someone this practice&rsquo;s queue</h3>
      <p className="sq-sub">
        A zip with the approved runbook, the commands, and which API key to create.
        The skill itself is not in it — the README says how to check it out at the
        revision this bundle was built for.
      </p>

      {loading && !bundle && <p className="sq-muted">Checking…</p>}

      {error && (
        <div className="sq-message error" role="alert">
          <span>{error}</span>
        </div>
      )}

      {bundle && bundle.available && (
        <>
          <div className="sq-bundle-facts">
            <span>
              Portal: <strong>{portalLabel(bundle.portal)}</strong>
            </span>
            <span>
              Runbook: <strong>version {bundle.runbook.version}</strong>
            </span>
            <span>
              Approved by {bundle.runbook.approvedBy}
              {bundle.runbook.approvedAt
                ? ` ${relativeTime(bundle.runbook.approvedAt)}`
                : ""}
            </span>
            <span>
              Built for {bundle.release.repo} {bundle.release.ref}
            </span>
          </div>
          <div className="sq-tab-toolbar">
            <button
              className="btn btn-primary btn-sm"
              onClick={download}
              disabled={downloading}
            >
              {downloading ? "Building…" : "Download the bundle"}
            </button>
            <span className="sq-muted">
              Re-download it after every approved runbook change: the runner refuses a
              version Atlas does not hold.
            </span>
          </div>

          {downloadError && (
            <div className="sq-message error" role="alert">
              <span>{downloadError}</span>
            </div>
          )}
          <p className="sq-muted">
            The person running it needs an Atlas API key for this practice with the{" "}
            <code>{bundle.scopes.join(", ") || "queue:work"}</code> scope, created
            under <strong>Practice Details → API keys</strong>, where it is shown
            once. It goes in their environment — never in chat or on a command line.
          </p>
        </>
      )}

      {bundle && !bundle.available && (
        <div className="sq-message warn">
          <span>
            {bundle.message}
            {NEXT_STEP[bundle.reason] ? ` ${NEXT_STEP[bundle.reason]}` : ""}{" "}
            {bundle.discovery.url && (
              <a
                href={bundle.discovery.url}
                target="_blank"
                rel="noreferrer noopener"
              >
                {bundle.discovery.section || "Discovery instructions"}
              </a>
            )}
          </span>
        </div>
      )}
    </section>
  );
}
